import { z } from 'zod';
import { WORKSPACE_ROLES } from './roles';
import { LEAD_SOURCES } from './channels';
import { FIELD_TYPES } from './templates';

const email = z.string().trim().toLowerCase().email('Enter a valid email address').max(254);
const password = z.string().min(10, 'Use at least 10 characters').max(200, 'Use at most 200 characters');
export const objectIdSchema = z.string().regex(/^[a-f0-9]{24}$/i, 'Invalid id');
const objectId = objectIdSchema;

export const LEAD_STATUSES = ['new', 'replied', 'duplicate', 'needs_attention'] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];
export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'New',
  replied: 'Replied',
  duplicate: 'Duplicate',
  needs_attention: 'Needs attention',
};

// ---------- Auth ----------
export const signUpSchema = z.object({
  name: z.string().trim().min(1, 'Enter your name').max(100),
  email,
  password,
  /** Private signup link key (SIGNUP_KEY), or a pending invitation for this email. */
  signupKey: z.string().max(200).optional(),
  inviteToken: z.string().max(200).optional(),
});
export type SignUpInput = z.infer<typeof signUpSchema>;

export const signInSchema = z.object({ email, password: z.string().min(1, 'Enter your password').max(200) });
export type SignInInput = z.infer<typeof signInSchema>;

export const forgotPasswordSchema = z.object({ email });
export const resetPasswordSchema = z.object({ token: z.string().min(20).max(200), password });
export const updateProfileSchema = z.object({ name: z.string().trim().min(1).max(100) });
export const changePasswordSchema = z.object({ currentPassword: z.string().min(1).max(200), newPassword: password });

// ---------- Workspaces ----------
const timezone = z
  .string()
  .min(1)
  .max(64)
  .refine((tz) => {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  }, 'Unknown timezone');

const countryCode = z.string().regex(/^[A-Z]{2}$/, 'Use a two-letter country code');

export const createWorkspaceSchema = z.object({
  name: z.string().trim().min(1, 'Enter a workspace name').max(80),
  timezone,
  defaultCountry: countryCode.optional(),
  industryTemplate: z.string().max(40).optional(),
  demo: z.boolean().optional(),
});
export type CreateWorkspaceInput = z.input<typeof createWorkspaceSchema>;

/** Which inquiries members with the Agent role can see. Admins, owners and viewers see all. */
export const LEAD_VISIBILITY = ['own', 'own_and_unassigned', 'all'] as const;
export type LeadVisibility = (typeof LEAD_VISIBILITY)[number];

export const workspaceSettingsSchema = z.object({
  leadVisibility: z.enum(LEAD_VISIBILITY).optional(),
  name: z.string().trim().min(1).max(80).optional(),
  timezone: timezone.optional(),
  defaultCountry: countryCode.nullable().optional(),
  industryTemplate: z.string().max(40).optional(),
  contextLabel: z.string().trim().min(1).max(40).optional(),
  identity: z
    .object({
      /** Auto-link inquiries whose normalized email exactly matches an existing customer. */
      autoLinkEmail: z.boolean(),
      /** Auto-link inquiries whose valid E.164 phone exactly matches an existing customer. */
      autoLinkPhone: z.boolean(),
      /** Repeat inquiries from the same confirmed customer about the same subject within this window are duplicates. */
      duplicateWindowHours: z.number().int().min(1).max(24 * 30),
    })
    .partial()
    .optional(),
});

export const inviteMemberSchema = z.object({
  email,
  role: z.enum(WORKSPACE_ROLES).refine((r) => r !== 'owner', 'Owners cannot be invited'),
});
export type InviteMemberInput = z.infer<typeof inviteMemberSchema>;
export const updateMemberSchema = z.object({ role: z.enum(WORKSPACE_ROLES) });

// ---------- Channels ----------
export const formFieldSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/, 'Use lowercase letters, numbers and underscores'),
  label: z.string().trim().min(1).max(80),
  type: z.enum(FIELD_TYPES),
  required: z.boolean().default(false),
  options: z.array(z.string().trim().min(1).max(80)).max(30).optional(),
  sensitive: z.boolean().optional(),
});

export const websiteFormConfigSchema = z.object({
  name: z.string().trim().min(1).max(80),
  heading: z.string().trim().max(120).default('Get in touch'),
  intro: z.string().trim().max(500).default(''),
  successMessage: z.string().trim().max(300).default('Thanks — we received your message and will reply soon.'),
  requirePhoneOrEmail: z.boolean().default(true),
  askPhone: z.boolean().default(true),
  consentText: z.string().trim().max(500).default(''),
  fields: z.array(formFieldSchema).max(20).default([]),
  /** Origins allowed to embed/post the form. Empty = any origin. */
  allowedOrigins: z.array(z.string().url().max(200)).max(20).default([]),
});
export type WebsiteFormConfig = z.output<typeof websiteFormConfigSchema>;

export const emailConnectionSchema = z.object({
  name: z.string().trim().min(1).max(80),
  /** Address customers write to; replies are sent from it. */
  address: email,
  /** smtp(s)://user:pass@host:port — stored encrypted. Optional: without it, replies open in the agent's mail client. */
  smtpUrl: z
    .string()
    .trim()
    .regex(/^smtps?:\/\//, 'Use an smtp:// or smtps:// URL')
    .max(500)
    .optional()
    .or(z.literal('')),
});

export const selectPagesSchema = z.object({
  pageIds: z.array(z.string().regex(/^\d+$/)).min(1, 'Select at least one Page').max(100),
});

// ---------- Inquiries ----------
export const inquiryContextInputSchema = z.object({
  type: z.string().trim().min(1).max(40).default('subject'),
  externalId: z.string().trim().max(200).optional(),
  title: z.string().trim().max(300).optional(),
  url: z.string().url().max(2048).optional(),
  metadata: z.record(z.string(), z.union([z.string().max(500), z.number(), z.boolean()])).optional(),
});

/** Public website form submission. Field names are stable for embedders. */
export const formSubmissionSchema = z.object({
  name: z.string().trim().max(150).optional().default(''),
  email: z.string().trim().max(254).optional().default(''),
  phone: z.string().trim().max(40).optional().default(''),
  message: z.string().trim().max(5000).optional().default(''),
  fields: z.record(z.string(), z.union([z.string().max(2000), z.number(), z.boolean()])).optional().default({}),
  context: inquiryContextInputSchema.optional(),
  consent: z.boolean().optional(),
  /** Client-supplied idempotency key so double-submits create one inquiry. */
  submissionId: z.string().trim().max(100).optional(),
  // Spam protection
  website: z.string().max(200).optional(), // honeypot — must be empty
  startedAt: z.number().int().optional(), // ms epoch when the form rendered
  sourceUrl: z.string().max(2048).optional(),
  utm: z.record(z.string(), z.string().max(200)).optional(),
});
export type FormSubmissionInput = z.input<typeof formSubmissionSchema>;

export const manualLeadSchema = z
  .object({
    name: z.string().trim().max(150).default(''),
    email: z.string().trim().max(254).default(''),
    phone: z.string().trim().max(40).default(''),
    message: z.string().trim().max(5000).default(''),
    sourceLabel: z.string().trim().max(80).optional(),
    context: inquiryContextInputSchema.optional(),
  })
  .refine((v) => v.name || v.email || v.phone, { message: 'Enter a name, email or phone', path: ['name'] });

export const listLeadsQuerySchema = z.object({
  status: z.enum(LEAD_STATUSES).optional(),
  source: z.enum(LEAD_SOURCES).optional(),
  connectionId: objectId.optional(),
  assignee: z.union([z.literal('me'), z.literal('unassigned'), objectId]).optional(),
  unread: z.enum(['true', 'false']).optional(),
  hasDuplicateWarning: z.enum(['true', 'false']).optional(),
  customerId: objectId.optional(),
  q: z.string().trim().max(100).optional(),
  before: z.string().datetime({ offset: true }).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});

/** Who receives new inquiries from a channel; null leaves them to assignment rules. */
export const assignConnectionSchema = z.object({ assigneeId: objectId.nullable() });

export const updateLeadSchema = z.object({
  status: z.enum(LEAD_STATUSES).optional(),
  assigneeId: objectId.nullable().optional(),
  read: z.boolean().optional(),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
});

export const replySchema = z.object({
  text: z.string().trim().min(1, 'Write a reply').max(5000),
  /** Explicit confirmation after a "previous response" warning. */
  acknowledgeWarning: z.boolean().optional(),
  subject: z.string().trim().max(200).optional(),
});

export const recordResponseSchema = z.object({
  method: z.enum(['open_original', 'manual']),
  status: z.enum(['initiated', 'marked_complete']),
  note: z.string().trim().max(1000).optional(),
});

export const noteSchema = z.object({ body: z.string().trim().min(1, 'Write a note').max(5000) });

// ---------- Customers ----------
export const listCustomersQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  hasDuplicates: z.enum(['true', 'false']).optional(),
  before: z.string().datetime({ offset: true }).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});

export const updateCustomerSchema = z.object({
  displayName: z.string().trim().min(1).max(150).optional(),
});

export const addIdentifierSchema = z.object({
  type: z.enum(['email', 'phone']),
  value: z.string().trim().min(3).max(254),
});

export const mergeCustomersSchema = z.object({
  /** Customer that remains. */
  survivorId: objectId,
  /** Customer folded into the survivor (kept, marked merged, reversible). */
  mergedId: objectId,
  reason: z.string().trim().max(500).optional(),
  candidateId: objectId.optional(),
});

export const resolveCandidateSchema = z.object({ action: z.literal('dismiss') });

// ---------- Automations ----------
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM');

export const workingHoursSchema = z.object({
  enabled: z.boolean(),
  days: z.array(z.number().int().min(0).max(6)).max(7),
  start: hhmm,
  end: hhmm,
});

/** `workflow` rules (M4) are validated by `workflowRuleSchema` in operations.ts. */
export const AUTOMATION_TYPES = ['acknowledgement', 'assignment', 'previous_contact_alert', 'workflow'] as const;
export type AutomationType = (typeof AUTOMATION_TYPES)[number];

export const automationRuleSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('acknowledgement'),
    name: z.string().trim().min(1).max(80),
    enabled: z.boolean(),
    /** Connection ids the rule applies to. Empty = all channels that can deliver. */
    connectionIds: z.array(objectId).max(100),
    message: z.string().trim().min(1).max(1000),
    emailSubject: z.string().trim().max(200).default('We received your inquiry'),
    cooldownHours: z.number().int().min(0).max(24 * 30),
    suppressRepeat: z.boolean(),
    workingHours: workingHoursSchema,
    outsideHoursBehavior: z.enum(['send', 'skip']),
  }),
  z.object({
    type: z.literal('assignment'),
    name: z.string().trim().min(1).max(80),
    enabled: z.boolean(),
    connectionIds: z.array(objectId).max(100),
    mode: z.enum(['round_robin', 'specific']),
    userIds: z.array(objectId).min(1).max(100),
    /** Keep returning customers with the agent who handled them last. */
    preferPreviousAssignee: z.boolean(),
  }),
  z.object({
    type: z.literal('previous_contact_alert'),
    name: z.string().trim().min(1).max(80),
    enabled: z.boolean(),
    connectionIds: z.array(objectId).max(100),
    /** Mark inquiries from customers with prior contact as needing attention. */
    markNeedsAttention: z.boolean(),
  }),
]);
export type AutomationRuleInput = z.input<typeof automationRuleSchema>;

export const automationPreviewSchema = z.object({
  ruleId: objectId,
  leadId: objectId,
});

export const listActivityQuerySchema = z.object({
  leadId: objectId.optional(),
  customerId: objectId.optional(),
  category: z.enum(['inquiry', 'identity', 'automation', 'response', 'team', 'channel']).optional(),
  before: z.string().datetime({ offset: true }).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

/** Meta lead ads: which Instant Forms to import and whether importing is on. */
export const metaLeadAdsSettingsSchema = z.object({
  syncEnabled: z.boolean().optional(),
  formMode: z.enum(['all', 'selected']).optional(),
  formIds: z.array(z.string().regex(/^\d+$/)).max(500).optional(),
});
export type MetaLeadAdsSettings = z.infer<typeof metaLeadAdsSettingsSchema>;

/** Meta lead ads: fetch leads Meta still holds for the selected forms (bounded). */
export const metaLeadAdsBackfillSchema = z.object({
  days: z.number().int().min(1).max(90).default(7),
});
